// "Continue in the app" links (2026-11-01): the website's Trip Builder (andiamotravelapp.com) sends the trip
// exactly as the visitor arranged it, e.g.
//   andiamoplaces.com/?plan=<guide_info_id>:<day>|<day>~<guide_info_id>:…&start=2026-11-02&src=trip-builder
// each day "<placeId>.<minutes>.<HHMM>-…" (tripBuilder.js appPlanLink writes it). A visit may end with "!<n>"
// (2026-11-01): n more days in that city than its ready-made guide has, which Build My Trip plans after the
// trip opens (planExtraDestinations). A visit starting with "+" (2026-11-03) shares the day of the visit before it:
// an afternoon half day after a morning one. The app opens it as a new
// guide built from those Featured guides: every place of each city guide (so Swap/Replace has the rest of
// the city to offer), the trip's stops as the "Main" schedule, one day after another from the start date
// (default tomorrow). Nothing is saved until the person saves the guide.
import { DateTime } from "luxon";
import { cleanDestination } from "./DestinationValidation";

const MAX_VISITS = 12;
const MAX_DAYS = 30;
const ID = /^[A-Za-z0-9_]{1,40}$/;

// True while the page was opened from a "Continue in the app" link whose trip hasn't opened yet (2026-11-03):
// the app then skips opening the usual start guide (the last one read, or the default), which used to load and
// show first, only to be replaced by the trip a few seconds later. App.js sets window.__planLinkDone once the
// trip (or, if it can't be built, the default guide) is open.
export function planLinkPending(search = typeof window !== "undefined" ? window.location.search : "") {
  return typeof window !== "undefined" && !window.__planLinkDone && parsePlanLink(search) != null;
}

// ?plan=… → { visits: [{ gid, days: [[{ placeId, time, minutes }]] }], start } or null
export function parsePlanLink(search) {
  let q;
  try { q = new URLSearchParams(search || ""); } catch (e) { return null; }
  const raw = q.get("plan");
  if (!raw) return null;
  let total = 0;
  const visits = raw.split("~").slice(0, MAX_VISITS).map((whole) => {
    const sameDay = whole.startsWith("+");
    const part = sameDay ? whole.slice(1) : whole;
    const i = part.indexOf(":");
    const gid = part.slice(0, i);
    if (i < 1 || !ID.test(gid)) return null;
    const bang = /!(\d{1,2})$/.exec(part);
    const extra = bang ? Math.min(Number(bang[1]), 14) : 0;
    const body = bang ? part.slice(i + 1, bang.index) : part.slice(i + 1);
    const days = body.split("|").slice(0, Math.max(0, MAX_DAYS - total)).map((d) =>
      (d ? d.split("-") : []).map((x) => /^([A-Za-z0-9_]{1,40})\.(\d{1,3})\.([01]\d|2[0-3])([0-5]\d)$/.exec(x)).filter(Boolean)
        .map((m) => ({ placeId: m[1], minutes: Math.min(Math.max(Number(m[2]), 5), 600), time: `${m[3]}:${m[4]}` }))
    );
    total += days.length + extra;
    return days.length ? { gid, days, extra, ...(sameDay ? { sameDay } : {}) } : null;
  }).filter(Boolean);
  if (!visits.length) return null;
  const start = /^\d{4}-\d{2}-\d{2}$/.test(q.get("start") || "") && DateTime.fromISO(q.get("start")).isValid ? q.get("start") : null;
  return { visits, start };
}

// The new guide's data (the stored guide_data shape the app decodes: decodeGuideFb) from the parsed link and
// the city guides' data ({ guide_info_id: guide_data }). A visit whose guide is missing is left out; a stop
// whose place is missing is dropped. Returns null when nothing is left.
export function buildPlanGuide(parsed, guides, { today = DateTime.utc() } = {}) {
  if (!parsed) return null;
  const visits = parsed.visits.filter((v) => guides[v.gid]?.places?.placesList);
  if (!visits.length) return null;
  // categories: one list, by name (each guide numbers its own); places keep theirs, renumbered
  const categories = [];
  const catId = (c) => {
    let k = categories.findIndex((x) => x.name === c.name);
    if (k < 0) { categories.push({ ...c, id: String(categories.length) }); k = categories.length - 1; }
    return String(k);
  };
  // Place IDs are only unique within a guide (2026-11-03: Montepulciano's afternoon showed Rome's St. Peter's
  // Square, its place having the same ID as one in the Rome guide). Each guide's places are looked up by
  // (guide, ID); a place whose ID is already taken by a different place gets a new ID. The same place in two
  // guides (same name, within about 100 m) is kept once.
  const places = [];
  // one mosaic group per city (2026-11-04): the mosaic shows Rome, Florence, Venice… plus "All places".
  // The group's cover is the city guide's cover.
  const groups = [];
  const groupOf = (gid) => {
    const a = guides[gid].attributes || {};
    const name = (a.cities || [])[0] || a.guide_name || "";
    if (!name) return null;
    let g = groups.find((x) => x.name === name);
    if (!g) {
      g = { id: String(groups.length), name, poi_img: "", alias: [], mosaic: true, schedule: "", image: a.image_m || a.image || "" };
      groups.push(g);
    }
    return g.id;
  };
  const seen = new Set();
  const byId = {};
  const idOf = {}; // "<gid>|<place ID in that guide>" → ID in the new guide
  const same = (a, b) => a.Name === b.Name && Math.abs(Number(a.Lat) - Number(b.Lat)) < 0.001 && Math.abs(Number(a.Longi) - Number(b.Longi)) < 0.001;
  let next = 1;
  const freeId = () => { while (byId[String(next)]) next++; return String(next); };
  visits.forEach((v) => {
    if (seen.has(v.gid)) return;
    seen.add(v.gid);
    const g = guides[v.gid];
    const cats = Object.fromEntries((g.categories || []).map((c) => [String(c.id), c]));
    const grp = groupOf(v.gid);
    g.places.placesList.forEach((p) => {
      const key = `${v.gid}|${p.ID}`, taken = byId[String(p.ID)];
      if (taken && same(taken, p)) {
        idOf[key] = String(p.ID);
        if (grp != null && !taken.Group.includes(grp)) taken.Group.push(grp);   // in both cities' tiles
        return;
      }
      const id = taken ? freeId() : String(p.ID);
      const copy = { ...p, ID: taken ? id : p.ID, Group: grp == null ? [] : [grp], Category: (p.Category || []).map((c) => cats[String(c)]).filter(Boolean).map(catId) };
      byId[id] = copy;
      idOf[key] = id;
      places.push(copy);
    });
  });

  let day = parsed.start ? DateTime.fromISO(parsed.start, { zone: "utc" }) : today.setZone("utc").startOf("day").plus({ days: 1 });
  const events = [];
  let dayNum = 0;
  const cities = [];
  visits.forEach((v) => {
    const city = (guides[v.gid].attributes?.cities || [])[0] || "";
    if (city && cities[cities.length - 1] !== city) cities.push(city);
    if (v.sameDay && dayNum) { day = day.minus({ days: 1 }); dayNum--; } // the afternoon of the day before
    v.days.forEach((stops) => {
      dayNum++;
      stops.forEach((s, k) => {
        const p = byId[idOf[`${v.gid}|${s.placeId}`]];
        if (!p) return;
        const [h, m] = s.time.split(":").map(Number);
        const st = day.set({ hour: h, minute: m, second: 0, millisecond: 0 });
        events.push({ ID: String(events.length + 1), Subject: p.Name, Places: String(p.ID), Start: st.toISO(), End: st.plus({ minutes: s.minutes }).toISO(),
          Itinerary: "Main", IsTravel: false, Body: "", Source: 0, CommentKey: "", dayNum, order_index: String(k + 1) });
      });
      day = day.plus({ days: 1 });
    });
    day = day.plus({ days: v.extra || 0 });
  });
  if (!events.length) return null;
  // the trip's cities as the app's destinations; the ready-made days count as built, so Build My Trip
  // (planExtraDestinations) only plans the extra days
  let d0 = parsed.start ? DateTime.fromISO(parsed.start, { zone: "utc" }) : today.setZone("utc").startOf("day").plus({ days: 1 });
  const destinations = [];
  visits.forEach((v, k) => {
    const a = guides[v.gid].attributes || {};
    if (v.sameDay && k) d0 = d0.minus({ days: 1 });
    destinations.push({ id: k + 1, city: (a.cities || [])[0] || "", country: (a.countries || [])[0] || "", dateStart: d0.set({ hour: 9 }).toISO(), dateEnd: d0.plus({ days: v.days.length - 1 }).set({ hour: 20 }).toISO(), built: true });
    d0 = d0.plus({ days: v.days.length + (v.extra || 0) });
  });
  const first = guides[visits[0].gid];
  const fa = first.attributes || {};
  const now = today.toUTC().toISO();
  const union = (k) => [...new Set(visits.flatMap((v) => guides[v.gid].attributes?.[k] || []))];
  const total = dayNum + visits.reduce((n, v) => n + (v.extra || 0), 0);
  const name = `${total}-day trip: ${(cities.length ? cities : ["My trip"]).join(" → ")}`;
  return {
    places: { placesList: places },
    schedule: { events },
    categories,
    groups,
    chapters: [],
    custom_icons: [],
    document: "",
    destinations,
    guide_settings: first.guide_settings,
    // a new guide's attributes (as GuideAttributes makes them): not Featured, nobody's yet, created now
    attributes: { guide_name: name, description: "", image: fa.image || "", image_m: fa.image_m || "", language: fa.language || "English", access: [], rating: 0,
      owner_name: "", user_id: "", creator_id: fa.creator_id || "", countries: union("countries"), cities: union("cities"), mod: now, created: now, read: now, copy: now,
      num_ratings: 0, times_read: 0, times_copied: 0, base_location: fa.base_location || { latitude: places[0].Lat, longitude: places[0].Longi }, type: "" },
  };
}

// The extra days (a visit's "!n") as Build My Trip destinations (as buildLink.js makes them), each right after
// that city's ready-made days; [] when there are none.
export function planExtraDestinations(parsed, guides, { today = DateTime.utc(), startH = 9, endH = 20 } = {}) {
  if (!parsed) return [];
  let day = parsed.start ? DateTime.fromISO(parsed.start, { zone: "utc" }) : today.setZone("utc").startOf("day").plus({ days: 1 });
  const out = [];
  parsed.visits.filter((v) => guides[v.gid]?.places?.placesList).forEach((v, k) => {
    if (v.sameDay && k) day = day.minus({ days: 1 });
    day = day.plus({ days: v.days.length });
    if (v.extra) {
      const a = guides[v.gid].attributes || {};
      out.push(cleanDestination({ id: Date.now() + k, city: (a.cities || [])[0] || "", country: (a.countries || [])[0] || "", dateStart: day.set({ hour: startH }), dateEnd: day.plus({ days: v.extra - 1 }).set({ hour: endH }) }));
      day = day.plus({ days: v.extra });
    }
  });
  return out;
}
